import { useEffect, useMemo, useState } from 'react';

import moment from 'moment';
import momentTz from 'moment-timezone';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import {
  DillPaymentMethod,
  DillTransaction,
  DillTransactionStatus,
  formatMoney,
  isUserSupplierAdmin,
} from '@dill/dill-shared';

import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { AppPaidInvoicesModal } from '../../../general/AppPaidInvoicesModal/AppPaidInvoicesModal';
import { AppExternalPaymentDetailsModal } from '../../../general/AppExternalPaymentDetailsModal/AppExternalPaymentDetailsModal';
import {
  getSupplierPaymentsFiles,
  getSupplierPaymentMethods,
  getSupplierRegistrationStatus,
  getSupplierTransactions,
  getTransactionById,
} from '../../../../redux/services/paymentsService';
import AppTransactionStatusPill from '../../../general/AppTransactionStatusPill/AppTransactionStatusPill';
import { SupplierAddWillCallPaymentModal } from '../SupplierAddWillCallPaymentModal/SupplierAddWillCallPaymentModal';
import AppAddPaymentMethodModal from '../../../general/AppAddPaymentMethodModal/AppAddPaymentMethodModal';
import SupplierWithdrawModal from '../../suppliers/SupplierWithdrawModal/SupplierWithdrawModal';
import AppVerifyDwollaMicroTransactionsModal from '../../../general/AppVerifyDwollaMicroTransactionsModal/AppVerifyDwollaMicroTransactionsModal';
import AppGetPortalLinkModal from '../../../general/AppGetPortalLinkModal/AppGetPortalLinkModal';

import './SupplierPaymentTable.scss';
import AppDateRangePickerModal from '../../../general/AppDateRangePickerModal/AppDateRangePickerModal';
import {
  DillTable,
  DillButton,
  DillDropDownFilter,
  DillCheckbox,
  DillModal,
  type DillSummaryCard,
  DillColumn,
} from '../../../general/Dill';
import { ArrowDownCircle, ArrowUpCircle } from 'react-feather';
import {
  ArrowBendDoubleUpRight,
  ArrowDownLeft,
  ArrowURightDown,
  Bank,
  Coins,
  CreditCard,
  Flag,
  Plus,
  Warning,
  Calendar,
  CurrencyCircleDollar,
  Link as LinkIcon,
  Receipt,
  Money,
  PaperPlaneRight,
  Storefront,
} from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import { buildSupplierPath } from '../../../../utils/helpers';
import { ClockCountdown } from '@phosphor-icons/react';

// The 4 payment "types" this table's Filters + row icon distinguish - a subset of
// DillTransactionTypesSchema (dillTransactionSchema.ts). Payouts/credits/fees aren't part of this
// grouping, so they're intentionally left out and simply show no type icon.
const PAYMENT_TYPE_META: Partial<Record<string, { icon: typeof Receipt; label: string }>> = {
  PAYMENT: { icon: Receipt, label: 'Invoice Payment' },
  FIXED_AMOUNT_PAYMENT: { icon: Money, label: 'Fixed Amount Payment' },
  PAYMENT_REQUEST_PAYMENT: { icon: PaperPlaneRight, label: 'Payment Request Payment' },
  WILL_CALL_PAYMENT: { icon: Storefront, label: 'Virtual POS Payment' },
};

interface PaymentRow {
  id: string;
  name: string;
  amount: string;
  createdAt: string;
  processedDate: string;
  transactionStatus: DillTransactionStatus | undefined;
  reconciliationStatus: DillTransactionStatus | undefined;
  method: string;
  methodType: string;
  cardType?: string;
  paymentMethodName?: string;
  withdrawTransactionId?: string;
  partialPaymentReason?: string;
  type: string;
  invoiceNumbers?: string[];
  isFeeTakenMonthly: boolean;
  isFiservAdjustment: boolean;
  // Paid outside of Dill (e.g. a check recorded in the ERP)
  isExternalPayment: boolean;
  externalReferenceNumber?: string;
}

export const SupplierPaymentTable = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const { totalSupplierBalance, supplierPaymentMethods, totalAmountForPendingTransactions } =
    useAppSelector((state) => state.payments);
  const [openPaidInvoiceDetails, setOpenPaidInvoiceDetails] = useState(false);
  const [isSupplierAddWillCallPaymentModal, setIsSupplierAddWillCallPaymentModal] = useState(false);
  const [openGetPortalLinkModal, setOpenGetPortalLinkModal] = useState(false);
  const { supplierTransactions } = useAppSelector((state) => state.payments);
  const [isAppDateRangePickerModalOpen, setIsAppDateRangePickerModalOpen] = useState(false);
  const [isAppAddPaymentMethodModalOpen, setIsAppAddPaymentMethodModalOpen] = useState(false);
  const [isShowPaymentMethodsModalOpen, setIsShowPaymentMethodsModalOpen] = useState(false);
  const [isSupplierWithdrawModalOpen, setIsSupplierWithdrawModalOpen] = useState(false);
  const [hoveredPayoutId, setHoveredPayoutId] = useState('');
  const [
    isAppVerifyDwollaMicroTransactionsModalOpen,
    setIsAppVerifyDwollaMicroTransactionsModalOpen,
  ] = useState(false);
  const [
    selectedPaymentMethodToVerifyMicroTransactions,
    setSelectedPaymentMethodToVerifyMicroTransactions,
  ] = useState<DillPaymentMethod>();
  const [isPaymentMethodForWithdrawingFeesOnly, setIsPaymentMethodForWithdrawingFeesOnly] =
    useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<DillTransaction | null>(null);
  const [selectedExternalPayment, setSelectedExternalPayment] = useState<DillTransaction | null>(
    null
  );
  const [dateRange, setDateRange] = useState<{
    startDate?: Date;
    endDate?: Date;
  }>({
    startDate: moment(new Date()).subtract(3, 'months').toDate(),
    endDate: new Date(),
  });
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const validTimezone = momentTz.tz.zone(timezone) ? timezone : 'America/Los_Angeles';
  const [searchText, setSearchText] = useState('');

  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] } | null>(null);

  const handleShowPaidInvoices = async (payment: DillTransaction) => {
    if (payment.isExternalPayment) {
      setSelectedExternalPayment(payment);
      return;
    }
    const transactionResults = await dispatch(getTransactionById({ transactionId: payment.id }));
    let finalTransaction = payment;
    if (transactionResults.type === 'payments/getTransactionById/fulfilled') {
      const res = transactionResults?.payload as any;
      finalTransaction = res.data as DillTransaction;
    }
    setSelectedTransaction(finalTransaction);
    setOpenPaidInvoiceDetails(true);
  };

  useEffect(() => {
    if (location.pathname.startsWith(buildSupplierPath(currentSupplier?.id, '/payments'))) {
      const params = new URLSearchParams(location.search);
      const transactionId = params.get('transactionId');
      if (transactionId) {
        const transaction = supplierTransactions.find((trans) => trans.id === transactionId);
        if (transaction) {
          handleShowPaidInvoices(transaction);
        }
      }
    }
  }, [location.pathname, supplierTransactions, location.search, currentSupplier?.id]);

  const handleExportTransactions = async () => {
    await dispatch(
      getSupplierPaymentsFiles({
        supplierId: currentSupplier?.id || '',
        startDate: dateRange?.startDate ? moment(dateRange?.startDate).format('MMM DD, YYYY') : '',
        endDate: dateRange?.endDate ? moment(dateRange?.endDate).format('MMM DD, YYYY') : '',
        timezone: validTimezone,
      })
    );
  };

  const handleViewMainPayment = (val: string) => {
    const transaction = supplierTransactions.find((trans) => trans.id === val);
    if (transaction) {
      navigate(buildSupplierPath(currentSupplier?.id, `/payments?transactionId=${val}`));
      handleShowPaidInvoices(transaction);
    }
  };
  useEffect(() => {
    if (dateRange && user && currentSupplier?.id) {
      dispatch(
        getSupplierTransactions({
          supplierId: currentSupplier?.id || '',
          startDate: dateRange?.startDate
            ? moment(dateRange?.startDate).format('MMM DD, YYYY')
            : '',
          endDate: dateRange?.endDate ? moment(dateRange?.endDate).format('MMM DD, YYYY') : '',
          timezone: validTimezone,
        })
      );
    }

    return () => {};
  }, [dateRange, user?.id, currentSupplier?.id]);

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(getSupplierPaymentMethods({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id]);

  const paymentMethodTypesFilterOptions = [
    { label: 'Card Only', value: 'CARD_ONLY' },
    { label: 'Bank ACH Only', value: 'BANK_ACH_ONLY' },
  ];

  const supplierTransactionsFiltered = useMemo(() => {
    let finalFiltered = supplierTransactions;

    // Destructure from unified filter state
    const selectedCustomers = selectedFilters?.customers ?? [];
    const selectedPaymentMethods = selectedFilters?.status ?? [];
    const selectedPaymentTypes = selectedFilters?.paymentType ?? [];
    const selectedOther = selectedFilters?.other ?? [];
    const activeJobs = selectedFilters?.jobs ?? [];
    const selectedSources = selectedFilters?.source ?? [];

    const hasActiveFilters =
      selectedSources.length > 0 ||
      selectedCustomers.length > 0 ||
      selectedPaymentMethods.length > 0 ||
      selectedPaymentTypes.length > 0 ||
      selectedOther.length > 0 ||
      activeJobs.length > 0;

    if (supplierTransactions.length > 0 && hasActiveFilters) {
      finalFiltered = supplierTransactions.filter((transaction) => {
        const filters: {
          isMatchPaymentMethodTypeFilter?: boolean;
          isMatchPaymentTypeFilter?: boolean;
          isMatchOtherFilter?: boolean;
          isMatchCustomerFilter?: boolean;
          isMatchJobFilter?: boolean;
          isMatchSourceFilter?: boolean;
        } = {};

        if (selectedSources.length > 0) {
          filters.isMatchSourceFilter = selectedSources.includes(
            transaction.isExternalPayment ? 'EXTERNAL' : 'DILL'
          );
        }

        // Payment method filter (was selectedPaymentMethodTypeFilterOption)
        if (selectedPaymentMethods.length > 0) {
          filters.isMatchPaymentMethodTypeFilter =
            (transaction?.paymentType === 'BANK' &&
              selectedPaymentMethods.includes('BANK_ACH_ONLY')) ||
            (transaction?.paymentType === 'CARD' && selectedPaymentMethods.includes('CARD_ONLY'));
        }

        // Other filter (was selectedOtherFilterOption)
        if (selectedOther.length > 0) {
          filters.isMatchOtherFilter =
            (selectedOther.includes('PAYOUTS') && transaction?.type === 'WITHDRAW') ||
            (selectedOther.includes('CREDITS') && transaction?.type === 'CREDIT_PAYMENT') ||
            (selectedOther.includes('PAYMENTS') &&
              transaction?.type !== 'CREDIT_PAYMENT' &&
              transaction?.type !== 'WITHDRAW') ||
            (selectedOther.includes('FEES') &&
              ((transaction?.type === 'WITHDRAW' &&
                (transaction?.isFeeTakenMonthly || transaction?.isFiservAdjustment)) ||
                transaction?.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
                transaction?.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER')) ||
            (selectedOther.includes('PAYMENTS_PAID_OUT') &&
              transaction?.type !== 'CREDIT_PAYMENT' &&
              transaction?.type !== 'WITHDRAW' &&
              !!transaction?.withdrawTransactionId) ||
            (selectedOther.includes('PAYMENTS_NOT_PAID_OUT') &&
              !transaction?.isExternalPayment &&
              transaction?.type !== 'CREDIT_PAYMENT' &&
              transaction?.type !== 'WITHDRAW' &&
              !transaction?.withdrawTransactionId &&
              transaction.transactionStatus === 'processed') ||
            (selectedOther.includes('RECONCILIATION_PENDING') &&
              transaction?.reconciliationStatus === 'pending') ||
            (selectedOther.includes('RECONCILIATION_FAILED') &&
              transaction?.reconciliationStatus === 'failed') ||
            (selectedOther.includes('RECONCILIATION_PROCESSED') &&
              transaction?.reconciliationStatus === 'processed') ||
            (selectedOther.includes('RECONCILIATION_WAITING') &&
              transaction?.reconciliationStatus === 'waiting') ||
            (selectedOther.includes('TRANSACTION_PENDING') &&
              transaction?.transactionStatus === 'pending') ||
            (selectedOther.includes('TRANSACTION_FAILED') &&
              transaction?.transactionStatus === 'failed') ||
            (selectedOther.includes('TRANSACTION_PROCESSED') &&
              transaction?.transactionStatus === 'processed') ||
            (selectedOther.includes('PARTIAL_PAYMENTS') && !!transaction?.partialPaymentReason);
        }

        // Payment type filter (Invoice / Fixed Amount / Payment Request / Virtual POS)
        if (selectedPaymentTypes.length > 0) {
          filters.isMatchPaymentTypeFilter =
            (selectedPaymentTypes.includes('INVOICE_PAYMENT') && transaction?.type === 'PAYMENT') ||
            (selectedPaymentTypes.includes('FIXED_AMOUNT_PAYMENT') &&
              transaction?.type === 'FIXED_AMOUNT_PAYMENT') ||
            (selectedPaymentTypes.includes('PAYMENT_REQUEST_PAYMENT') &&
              transaction?.type === 'PAYMENT_REQUEST_PAYMENT') ||
            (selectedPaymentTypes.includes('VIRTUAL_POS_PAYMENT') &&
              transaction?.type === 'WILL_CALL_PAYMENT');
        }

        // Customer filter (was selectedCustomersFilterOption)
        if (selectedCustomers.length > 0) {
          filters.isMatchCustomerFilter =
            !!transaction.buyer && selectedCustomers.includes(transaction.buyer?.id);
        }

        if (activeJobs.length > 0) {
          filters.isMatchJobFilter = transaction.invoicePayments.some(
            (inv) => inv.job && activeJobs.includes(inv.job.id)
          );
        }

        return Object.values(filters).every((item) => item);
      });
    }

    // Search text
    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      finalFiltered = finalFiltered.filter((transaction) => {
        const isPayment =
          transaction.type === 'PAYMENT' ||
          transaction.type === 'WILL_CALL_PAYMENT' ||
          transaction.type === 'PAYMENT_REQUEST_PAYMENT' ||
          transaction.type === 'CREDIT_PAYMENT' ||
          transaction.type === 'FIXED_AMOUNT_PAYMENT';

        const totalAmount = isPayment
          ? `+${formatMoney(transaction?.totalAmountPaid - (transaction?.facilitationFee ?? 0), 2)}`
          : `-${formatMoney(transaction?.totalAmountPaid, 2)}`;

        const paymentMethodName =
          transaction.type === 'PAYMENT_REQUEST_PAYMENT' || transaction.type === 'WILL_CALL_PAYMENT'
            ? transaction?.paymentMethodName ?? ''
            : transaction?.paymentMethod?.paymentMethodName ?? '';

        return (
          transaction.invoicePayments.some((inv) =>
            inv.invoiceNumber.toLowerCase().includes(lowerSearch)
          ) ||
          transaction?.buyer?.name?.toLowerCase().includes(lowerSearch) ||
          transaction?.buyer?.quickbooksCustomerId?.toLowerCase().includes(lowerSearch) ||
          transaction?.recipient?.name?.toLowerCase().includes(lowerSearch) ||
          paymentMethodName.toLowerCase().includes(lowerSearch) ||
          totalAmount.toLowerCase().includes(lowerSearch) ||
          moment(transaction.createdAt).format('MM/DD/YYYY').includes(lowerSearch) ||
          (transaction.processedDate &&
            moment(transaction.processedDate).format('MM/DD/YYYY').includes(lowerSearch))
        );
      });
    }

    return finalFiltered;
  }, [supplierTransactions, selectedFilters, searchText]);

  const handleSelectFilter = (selected: { [key: string]: string[] }) => {
    setSelectedFilters(selected);
  };

  const paymentRows: PaymentRow[] = useMemo(() => {
    return supplierTransactionsFiltered.map((transaction) => {
      let buyerName = transaction?.buyer?.name ?? '';
      if (currentSupplier?.integrationType !== 'RUTTER_QBD') {
        buyerName = `${transaction?.buyer?.name ?? ''} - ${
          transaction?.buyer?.quickbooksCustomerId ?? ''
        }`;
      }

      const methodType =
        transaction.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
        transaction.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
          ? 'FEES'
          : transaction.type;

      let amount = '';
      const total = transaction.totalAmountPaid;
      if (
        methodType === 'PAYMENT' ||
        methodType === 'WILL_CALL_PAYMENT' ||
        methodType === 'PAYMENT_REQUEST_PAYMENT' ||
        methodType === 'CREDIT_PAYMENT' ||
        methodType === 'FIXED_AMOUNT_PAYMENT'
      ) {
        if (transaction?.transactionErrorMessage) {
          amount = `+${formatMoney(total, 2)}`;
        } else {
          if (transaction?.cardPaymentProvider === 'FISERV') {
            amount = `+${formatMoney(total, 2)}`;
          } else {
            amount = `+${formatMoney(total - (transaction?.facilitationFee ?? 0), 2)}`;
          }
        }
      } else {
        amount = `-${formatMoney(total, 2)}`;
        if (total > 0) {
          amount = `-${formatMoney(total, 2)}`;
        } else {
          amount = `${formatMoney(Math.abs(total), 2)}`;
        }
      }

      let name = '';
      if (
        transaction.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
        transaction.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
      ) {
        name = 'Dill';
      } else if (
        transaction.type === 'PAYMENT' ||
        transaction.type === 'CREDIT_PAYMENT' ||
        transaction.type === 'PAYMENT_REQUEST_PAYMENT' ||
        transaction.type === 'FIXED_AMOUNT_PAYMENT'
      ) {
        name = buyerName;
      } else if (transaction.type === 'WILL_CALL_PAYMENT' && transaction.buyer) {
        name = buyerName;
      } else {
        name = transaction?.recipient?.name ?? '';
      }

      return {
        id: transaction.id,
        name,
        amount,
        createdAt: moment(transaction.createdAt).format('MM/DD/YYYY'),
        processedDate: transaction.processedDate
          ? moment(transaction.processedDate).format('MM/DD/YYYY')
          : '',
        transactionStatus: transaction.transactionStatus,
        reconciliationStatus: transaction.reconciliationStatus,
        method: '',
        methodType,
        cardType: transaction.cardType ?? undefined,
        paymentMethodName: transaction.paymentMethodName ?? undefined,
        withdrawTransactionId: transaction.withdrawTransactionId ?? undefined,
        partialPaymentReason: transaction.partialPaymentReason ?? undefined,
        type: transaction.type,
        isFeeTakenMonthly: transaction?.isFeeTakenMonthly || false,
        isFiservAdjustment: transaction?.isFiservAdjustment || false,
        invoiceNumbers: transaction.invoicePayments.map((inv) => inv.invoiceNumber),
        isExternalPayment: !!transaction.isExternalPayment,
        externalReferenceNumber: transaction.externalReferenceNumber ?? undefined,
      };
    });
  }, [supplierTransactionsFiltered, currentSupplier?.integrationType, currentSupplier?.id]);

  const hasExternalPayments = useMemo(
    () => supplierTransactions.some((transaction) => transaction.isExternalPayment),
    [supplierTransactions]
  );

  const columns: DillColumn<PaymentRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: '',
        width: 150,
        sortable: false,
        getSearchValue: (row: PaymentRow) => [row.name, ...(row.invoiceNumbers ?? [])].join(' '),
        render: (_: any, row: PaymentRow) => {
          // console.log({ ddd: b });
          return (
            <div className="w-full flex items-center pr-1">
              <div className="mx-0.5">
                {row.type === 'WITHDRAW' && (
                  <div className="aspect-square rounded-full WARNING_100-BG p-2">
                    <ArrowUpCircle color={COLORS.WARNING_500} />
                  </div>
                )}
                {row.type !== 'WITHDRAW' && (
                  <div className="aspect-square rounded-full SUCCESS_100-BG p-2">
                    <ArrowDownCircle color={COLORS.SUCCESS_500} />
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs font-semibold word-wrap capitalize">
                  {row.name ? row.name.toLowerCase() : row.name}
                </div>
                <div className="flex items-center mt-0.5">
                  {row.methodType === 'PAYMENT' ||
                  row.methodType === 'WILL_CALL_PAYMENT' ||
                  row.methodType === 'PAYMENT_REQUEST_PAYMENT' ||
                  row.methodType === 'CREDIT_PAYMENT' ||
                  row.methodType === 'FIXED_AMOUNT_PAYMENT' ? (
                    <ArrowDownLeft color={COLORS.SUCCESS_500} size={10} />
                  ) : row.methodType === 'FEES' ? (
                    <ArrowURightDown color={COLORS.WARNING_500} size={10} />
                  ) : (
                    <ArrowDownLeft color={COLORS.ERROR_500} size={10} className="rotate-180" />
                  )}
                  <div className="text-2xs ml-1">
                    {`${
                      row.methodType === 'WITHDRAW' &&
                      (row.isFeeTakenMonthly || row.isFiservAdjustment)
                        ? 'FEES'
                        : row.type == 'WITHDRAW'
                        ? 'PAYOUT'
                        : 'PAYMENT'
                    }`}
                  </div>
                  {hasExternalPayments &&
                    row.type !== 'WITHDRAW' &&
                    row.methodType !== 'FEES' &&
                    (row.isExternalPayment ? (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold leading-none whitespace-nowrap">
                        Outside Dill
                      </span>
                    ) : (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded-full PRIMARY_100-BG PRIMARY_500-CLR text-[10px] font-semibold leading-none whitespace-nowrap">
                        Paid on Dill
                      </span>
                    ))}
                  {(() => {
                    const paymentTypeMeta = PAYMENT_TYPE_META[row.type];
                    if (!paymentTypeMeta) return null;
                    const { icon: PaymentTypeIcon, label: paymentTypeLabel } = paymentTypeMeta;
                    return (
                      <>
                        <span
                          className="ml-1.5 flex items-center"
                          data-tooltip-id={`payment-type-${row.id}`}>
                          <PaymentTypeIcon size={12} color={COLORS.PRIMARY_500} />
                        </span>
                        <ReactTooltip
                          id={`payment-type-${row.id}`}
                          place="top"
                          variant="dark"
                          opacity="100%"
                          style={{ borderRadius: '8px', fontSize: '12px', zIndex: 10000 }}>
                          {paymentTypeLabel}
                        </ReactTooltip>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        key: 'amount',
        label: 'Amount',
        width: 80,
        sortable: true,
        render: (_: any, row: PaymentRow) => (
          <div className="flex flex-col">
            <p className={'text-xs text-left w-full'}>{row.amount}</p>
            {row.withdrawTransactionId && (
              <p
                className="text-2xs text-left w-fit px-1.5 py-1 bg-green-500 mt-2 rounded-lg text-white cursor-pointer"
                onMouseEnter={() => setHoveredPayoutId(row.withdrawTransactionId || '')}
                onMouseLeave={() => setHoveredPayoutId('')}
                onClick={() => {
                  if (
                    location.pathname.startsWith(
                      buildSupplierPath(currentSupplier?.id, '/payments')
                    )
                  ) {
                    navigate(
                      buildSupplierPath(
                        currentSupplier?.id,
                        `/payments?transactionId=${row.withdrawTransactionId}`
                      )
                    );
                  }
                }}>
                Paid Out
              </p>
            )}
            {row.partialPaymentReason && (
              <p className="text-2xs text-left w-fit py-1 mt-0.5 rounded-lg text-white cursor-pointer">
                <Flag
                  size={14}
                  weight="fill"
                  className="text-red-400"
                  data-tooltip-id={`dispute-flag-${row.id}`}
                />
                <ReactTooltip
                  id={`dispute-flag-${row.id}`}
                  place="right"
                  variant="dark"
                  opacity="100%"
                  style={{
                    padding: '5px',
                    background: '#222',
                    width: '100px',
                    zIndex: 99999,
                  }}>
                  <p className="text-xs">Short payment</p>
                </ReactTooltip>
              </p>
            )}
          </div>
        ),
      },
      {
        key: 'createdAt',
        label: 'Date',
        width: 80,
        sortable: true,
      },
      {
        key: 'processedDate',
        label: 'Date Processed',
        width: 120,
        sortable: true,
        defaultSortDir: 'desc',
      },
      {
        key: 'transactionStatus',
        label: 'Transaction Status',
        width: 140,
        render: (_: any, row: PaymentRow) => (
          <div className="w-full mr-6">
            <AppTransactionStatusPill
              status={row.transactionStatus ?? ('pending' as DillTransactionStatus)}
            />
          </div>
        ),
      },
      {
        key: 'reconciliationStatus',
        label: 'Reconciliation Status',
        width: 150,
        render: (_: any, row: PaymentRow) => (
          <div className="w-full mr-6">
            <AppTransactionStatusPill
              status={row.reconciliationStatus ?? ('pending' as DillTransactionStatus)}
            />
          </div>
        ),
      },
      {
        key: 'method',
        label: 'Method',
        width: 170,
        render: (_: any, row: PaymentRow) => {
          if (row.isExternalPayment) {
            return (
              <div className="w-full flex">
                <div className="payment-type-logo">
                  <Money size={24} style={{ display: 'flex', alignSelf: 'center' }} />
                </div>
                <div>
                  <div className="text-xs font-bold">Paid outside Dill</div>
                  {row.externalReferenceNumber && (
                    <div className="payment-method-details">Ref #{row.externalReferenceNumber}</div>
                  )}
                </div>
              </div>
            );
          }
          const transaction = supplierTransactionsFiltered.find((t) => t.id === row.id);
          if (!transaction || row.methodType === 'FEES') return null;

          return (
            <div className="w-full flex">
              <div className="payment-type-logo">
                {transaction?.paymentMethod?.type === 'BANK_ACCOUNT' &&
                transaction.type === 'WITHDRAW' ? (
                  <ArrowBendDoubleUpRight
                    size={24}
                    style={{ display: 'flex', alignSelf: 'center' }}
                  />
                ) : transaction?.paymentMethod?.type === 'BANK_ACCOUNT' ? (
                  <Bank size={24} style={{ display: 'flex', alignSelf: 'center' }} />
                ) : transaction.type === 'CREDIT_PAYMENT' ? (
                  <Coins size={24} style={{ display: 'flex', alignSelf: 'center' }} />
                ) : transaction.type !== 'WITHDRAW' ? (
                  <CreditCard size={24} style={{ display: 'flex', alignSelf: 'center' }} />
                ) : null}
              </div>
              <div>
                {transaction?.paymentMethod?.type === 'BANK_ACCOUNT' &&
                transaction.type === 'WITHDRAW' ? (
                  <div>
                    <div className="text-xs font-bold">
                      Payout{` (${transaction.paymentType === 'CARD' ? 'Card' : 'Bank'})`}
                    </div>
                  </div>
                ) : transaction.type !== 'CREDIT_PAYMENT' &&
                  (transaction?.paymentMethod?.type === 'BANK_ACCOUNT' ||
                    transaction?.paymentType === 'BANK') ? (
                  <div className="text-xs font-bold">Bank Transfer</div>
                ) : transaction.type === 'CREDIT_PAYMENT' ? (
                  <div className="text-xs font-bold">Credits</div>
                ) : transaction?.cardType ? (
                  <div className="text-xs font-bold">
                    {transaction?.cardType === 'CREDIT' ? 'Credit Card' : 'Debit / Other Card'}
                  </div>
                ) : transaction?.paymentMethod?.cardType ? (
                  <div className="text-xs font-bold">
                    {transaction?.paymentMethod?.cardType === 'CREDIT'
                      ? 'Credit Card'
                      : 'Debit / Other Card'}
                  </div>
                ) : transaction.type !== 'WITHDRAW' ? (
                  <div className="text-xs font-bold">Credit/Debit Card</div>
                ) : null}
                <div className="payment-method-details">
                  {transaction?.paymentMethodName ??
                    transaction?.paymentMethod?.paymentMethodName ??
                    ''}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        key: 'viewDetails',
        label: '',
        width: 80,
        sortable: false,
        render: (_: any, row: PaymentRow) => {
          if (row.methodType === 'FEES') return null;
          const transaction = supplierTransactionsFiltered.find((t) => t.id === row.id);
          return (
            <div className="flex ml-1">
              <DillButton
                label="Details"
                variant="secondary"
                onClick={() => {
                  if (transaction) {
                    handleShowPaidInvoices(transaction);
                    if (
                      location.pathname.startsWith(
                        buildSupplierPath(currentSupplier?.id, '/payments')
                      )
                    ) {
                      navigate(
                        buildSupplierPath(currentSupplier?.id, `/payments?transactionId=${row.id}`)
                      );
                    }
                  }
                }}
              />
            </div>
          );
        },
      },
    ],
    [supplierTransactionsFiltered, location.pathname, navigate, hasExternalPayments]
  );

  const createCheckboxItem = ({
    label,
    value,
    isChecked,
  }: {
    label: string;
    value: string;
    isChecked: boolean;
  }) => ({
    label,
    value,
    labelInElement: (
      <div className="flex mx-1 my-1 items-center gap-2">
        <DillCheckbox isChecked={isChecked} />
        <div className="text-black text-xs">{label}</div>
      </div>
    ),
  });

  const paymentTypeFilterOptions = [
    { label: 'Invoice Payment', value: 'INVOICE_PAYMENT' },
    { label: 'Fixed Amount Payment', value: 'FIXED_AMOUNT_PAYMENT' },
    { label: 'Payment Request Payment', value: 'PAYMENT_REQUEST_PAYMENT' },
    { label: 'Virtual POS Payment', value: 'VIRTUAL_POS_PAYMENT' },
  ];

  const otherFilterOptions = [
    { label: 'Payouts', value: 'PAYOUTS' },
    { label: 'Credits', value: 'CREDITS' },
    { label: 'Payments', value: 'PAYMENTS' },
    { label: 'Fees', value: 'FEES' },
    { label: 'Payments Paid Out', value: 'PAYMENTS_PAID_OUT' },
    { label: 'Payments NOT Paid Out', value: 'PAYMENTS_NOT_PAID_OUT' },
    { label: 'Reconciliation Processed', value: 'RECONCILIATION_PROCESSED' },
    { label: 'Reconciliation Pending', value: 'RECONCILIATION_PENDING' },
    { label: 'Reconciliation Failed', value: 'RECONCILIATION_FAILED' },
    { label: 'Reconciliation Waiting', value: 'RECONCILIATION_WAITING' },
    { label: 'Transaction Processed', value: 'TRANSACTION_PROCESSED' },
    { label: 'Transaction Pending', value: 'TRANSACTION_PENDING' },
    { label: 'Transaction Failed', value: 'TRANSACTION_FAILED' },
    { label: 'Short Payments', value: 'PARTIAL_PAYMENTS' },
  ];

  const customersMap: { [key: string]: { label: string; value: string } } = {};
  supplierTransactions.forEach((transaction) => {
    if (transaction.buyer && !customersMap[transaction.buyer.id]) {
      customersMap[transaction.buyer.id] = {
        label: `${transaction.buyer.name} - ${transaction.buyer.quickbooksCustomerId}`,
        value: transaction.buyer.id,
      };
    }
  });
  const customersFilterOptions = Object.values(customersMap);

  const totalPending = supplierTransactions
    .filter((t) => t.transactionStatus === 'pending' && t.type !== 'WITHDRAW')
    .reduce((sum, t) => sum + (t.totalAmountPaid || 0), 0);

  const jobsMap: { [id: string]: { label: string; value: string } } = {};
  supplierTransactions.forEach((transaction) => {
    transaction.invoicePayments.forEach((inv) => {
      if (inv.job && !jobsMap[inv.job.id]) {
        jobsMap[inv.job.id] = {
          label: inv.job.jobNumber ? `${inv.job.name} - ${inv.job.jobNumber}` : inv.job.name,
          value: inv.job.id,
        };
      }
    });
  });
  const jobsFilterOptions = Object.values(jobsMap);

  const totalWaitingReconciliation = supplierTransactions
    .filter(
      (t) =>
        t.reconciliationStatus === 'pending' &&
        t.transactionStatus === 'processed' &&
        t.type !== 'WITHDRAW'
    )
    .reduce((sum, t) => sum + (t.totalAmountPaid || 0), 0);

  const pendingTransactionsCount = supplierTransactions.filter(
    (t) => t.transactionStatus === 'pending' && t.type !== 'WITHDRAW'
  ).length;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTransactions = supplierTransactions.filter((t) => {
    if (!t.createdAt || t.type === 'WITHDRAW' || t.isExternalPayment) return false;
    const transactionDate = new Date(t.createdAt);
    return transactionDate >= today;
  });
  const todayTransactionsCount = todayTransactions.length;
  const todayTransactionsAmount = todayTransactions.reduce(
    (sum, t) => sum + (t.totalAmountPaid || 0),
    0
  );

  const summaryCards: DillSummaryCard[] = [
    {
      label: 'Balance',
      value: formatMoney(totalSupplierBalance, 2),
      subLabel: `Total balance available`,
      icon: <CurrencyCircleDollar size={14} />,
      variant: 'success',
    },
    {
      label: 'Amount Pending',
      value: formatMoney(totalAmountForPendingTransactions, 2),
      subLabel: `${pendingTransactionsCount} Transactions`,
      icon: <ClockCountdown size={14} />,
      variant: totalPending > 0 ? 'warning' : 'default',
    },
    {
      label: 'Total Pending Reconciliation',
      value: formatMoney(totalWaitingReconciliation, 2),
      subLabel: `${
        supplierTransactions.filter(
          (t) =>
            t.reconciliationStatus === 'pending' &&
            t.transactionStatus === 'processed' &&
            t.type !== 'WITHDRAW'
        ).length
      } Transactions`,
      icon: <Warning size={14} />,
      variant: totalWaitingReconciliation > 0 ? 'warning' : 'default',
    },
    {
      label: 'Payments Initiated Today',
      value: formatMoney(todayTransactionsAmount, 2),
      subLabel: `${todayTransactionsCount} Transactions`,
      icon: <Coins size={14} />,
      variant: 'default',
    },
  ];

  const sourceFilterOptions = [
    { label: 'Paid on Dill', value: 'DILL' },
    { label: 'Paid outside Dill', value: 'EXTERNAL' },
  ];

  const filterItems = [
    ...(hasExternalPayments
      ? [
          {
            id: 'source',
            label: 'Source',
            value: sourceFilterOptions.map((option) =>
              createCheckboxItem({
                label: option.label,
                value: option.value,
                isChecked: selectedFilters?.source?.includes(option.value) ?? false,
              })
            ),
          },
        ]
      : []),
    {
      id: 'customers',
      label: 'Customers',
      isSearchable: true,
      value: customersFilterOptions.map((option) =>
        createCheckboxItem({
          label: option?.label ?? '',
          value: option?.value ?? '',
          isChecked: selectedFilters?.customers?.includes(option?.value ?? '') ?? false,
        })
      ),
    },
    {
      id: 'status',
      label: 'Payment methods',
      value: paymentMethodTypesFilterOptions.map((option) =>
        createCheckboxItem({
          label: option.label,
          value: option.value,
          isChecked: selectedFilters?.status?.includes(option.value) ?? false,
        })
      ),
    },
    {
      id: 'paymentType',
      label: 'Payment Type',
      value: paymentTypeFilterOptions.map((option) =>
        createCheckboxItem({
          label: option.label,
          value: option.value,
          isChecked: selectedFilters?.paymentType?.includes(option.value) ?? false,
        })
      ),
    },
    {
      id: 'other',
      label: 'Other',
      value: otherFilterOptions.map((option) =>
        createCheckboxItem({
          label: option.label,
          value: option.value,
          isChecked: selectedFilters?.other?.includes(option.value) ?? false,
        })
      ),
    },
    ...(jobsFilterOptions.length > 0
      ? [
          {
            id: 'jobs',
            label: 'Jobs',
            isSearchable: true,
            value: jobsFilterOptions.map((option) =>
              createCheckboxItem({
                label: option?.label ?? '',
                value: option?.value ?? '',
                isChecked: selectedFilters?.jobs?.includes(option?.value ?? '') ?? false,
              })
            ),
          },
        ]
      : []),
  ];

  const handleFilterChange = (data: Record<string, { value: string }[]>) => {
    const transformed = Object.fromEntries(
      Object.entries(data).map(([key, arr]) => [key, arr.map((item) => item.value)])
    );
    setSelectedFilters(transformed);
  };

  const toolbarLeft = (
    <div className="flex items-center gap-2">
      <DillButton
        variant="icon-default"
        onClick={() => setOpenGetPortalLinkModal(true)}
        icon={<LinkIcon size={16} color={COLORS.GREY_500} />}
        height={32}
        width={32}
      />
      <div className="flex flex-row items-center">
        <button
          onClick={() => setIsAppDateRangePickerModalOpen(true)}
          className="flex flex-row items-center GREY_300-BORDER border rounded py-1 px-2 WHITE-BG hover:bg-slate-200">
          <Calendar className="text-slate-500 mr-2" size={20} />
          <div className="text-xs TEXT_PRIMARY-CLR">{`${
            dateRange?.startDate ? moment(dateRange?.startDate).format('MMM DD, YYYY') : ''
          }-${dateRange?.endDate ? moment(dateRange?.endDate).format('MMM DD, YYYY') : ''}`}</div>
        </button>
      </div>
      <div className="w-[260px]">
        <DillDropDownFilter
          placeholder="Filters"
          closeMenu={false}
          isMulti
          showSelectedItems
          multiValue={selectedFilters}
          items={filterItems}
          showMinimalDesign
          onMultiSelectChange={handleFilterChange}
          inputHeight={31}
        />
      </div>
    </div>
  );

  const toolbarRight =
    isUserSupplierAdmin({ user, supplier: currentSupplier }) &&
    currentSupplier?.dwollaCustomerStatus === 'verified' ? (
      <div className="flex items-center gap-2">
        <p className="text-xs font-medium">Withdrawal Methods</p>

        <div className="flex items-center gap-2">
          {/* Existing Methods */}
          {supplierPaymentMethods.length > 0 &&
            (() => {
              const nonFeeMethod = supplierPaymentMethods.find((pm) => !pm.isWithdrawFeesOnly);

              return (
                <div
                  className="flex items-center gap-2 cursor-pointer GREY_300-BORDER border rounded px-3 py-1.5 hover:bg-gray-50"
                  onClick={() => setIsShowPaymentMethodsModalOpen(true)}>
                  <Bank size={18} className="text-gray-500" />
                  <span className="text-xs font-medium">
                    {nonFeeMethod?.paymentMethodName || 'Bank Account'}
                  </span>

                  {supplierPaymentMethods.length > 1 && (
                    <span className="text-xs text-gray-400">
                      +{supplierPaymentMethods.length - 1} more
                    </span>
                  )}
                </div>
              );
            })()}

          {/* Add Method */}
          {(() => {
            const hasMethods = supplierPaymentMethods.length > 0;
            const allowsSeparateFeeMethod =
              currentSupplier?.isSeparateFeeWithdrawalMethodEnabled === true;

            const hasWithdrawFeesOnly = supplierPaymentMethods.some((pm) => pm.isWithdrawFeesOnly);

            const isDisabled =
              (hasMethods && !allowsSeparateFeeMethod) ||
              (hasMethods && allowsSeparateFeeMethod && hasWithdrawFeesOnly);

            return (
              <DillButton
                label="Add Withdrawal Method"
                icon={<Plus size={16} />}
                isDisabled={isDisabled}
                onClick={() => {
                  if (hasMethods && allowsSeparateFeeMethod && !hasWithdrawFeesOnly) {
                    setIsPaymentMethodForWithdrawingFeesOnly(true);
                  } else {
                    setIsPaymentMethodForWithdrawingFeesOnly(false);
                  }

                  setIsAppAddPaymentMethodModalOpen(true);
                }}
              />
            );
          })()}
        </div>
      </div>
    ) : null;

  return (
    <div className="w-full h-full px-4 pb-4 overflow-hidden flex flex-col">
      <AppGetPortalLinkModal
        open={openGetPortalLinkModal}
        handleClose={() => setOpenGetPortalLinkModal(false)}
      />
      <AppExternalPaymentDetailsModal
        transaction={selectedExternalPayment}
        accountType="supplier"
        onClose={() => {
          setSelectedExternalPayment(null);
          if (location.pathname.startsWith(buildSupplierPath(currentSupplier?.id, '/payments'))) {
            navigate(buildSupplierPath(currentSupplier?.id, `/payments`));
          }
        }}
      />
      <AppPaidInvoicesModal
        open={openPaidInvoiceDetails}
        handleClose={(isReloadAllTransactionsOnClose) => {
          setOpenPaidInvoiceDetails(false);
          if (location.pathname.startsWith(buildSupplierPath(currentSupplier?.id, '/payments'))) {
            navigate(buildSupplierPath(currentSupplier?.id, `/payments`));
          }
          if (isReloadAllTransactionsOnClose) {
            dispatch(
              getSupplierTransactions({
                supplierId: currentSupplier?.id || '',
                startDate: dateRange?.startDate
                  ? moment(dateRange?.startDate).format('MMM DD, YYYY')
                  : '',
                endDate: dateRange?.endDate
                  ? moment(dateRange?.endDate).format('MMM DD, YYYY')
                  : '',
                timezone: validTimezone,
              })
            );
          }
        }}
        reloadSelectedTransaction={() => {
          if (selectedTransaction) {
            handleShowPaidInvoices(selectedTransaction);
          }
        }}
        accountType="supplier"
        selectedTransaction={selectedTransaction}
        onViewMainPayment={handleViewMainPayment}
      />
      <SupplierAddWillCallPaymentModal
        open={isSupplierAddWillCallPaymentModal}
        handleClose={() => {
          setIsSupplierAddWillCallPaymentModal(false);
        }}
        handlePaymentDone={() => {
          setIsSupplierAddWillCallPaymentModal(false);
          dispatch(
            getSupplierTransactions({
              supplierId: currentSupplier?.id || '',
              startDate: dateRange?.startDate
                ? moment(dateRange?.startDate).format('MMM DD, YYYY')
                : '',
              endDate: dateRange?.endDate ? moment(dateRange?.endDate).format('MMM DD, YYYY') : '',
              timezone: validTimezone,
            })
          );
        }}
      />
      <AppDateRangePickerModal
        open={isAppDateRangePickerModalOpen}
        selectedDateRange={dateRange}
        handleClose={() => {
          setIsAppDateRangePickerModalOpen(false);
        }}
        onSelectDateRange={(selection) => {
          setIsAppDateRangePickerModalOpen(false);
          setDateRange(selection);
        }}
      />
      <AppAddPaymentMethodModal
        open={isAppAddPaymentMethodModalOpen}
        paymentOptionsToShow="BANK"
        userType="supplier"
        isPaymentMethodForWithdrawingFeesOnly={isPaymentMethodForWithdrawingFeesOnly}
        handleClose={() => {
          setIsAppAddPaymentMethodModalOpen(false);
          if (currentSupplier?.id) {
            dispatch(getSupplierPaymentMethods({ supplierId: currentSupplier.id }));
          }
        }}
      />

      <DillModal
        open={isShowPaymentMethodsModalOpen}
        onClose={() => setIsShowPaymentMethodsModalOpen(false)}
        title="Withdrawal Methods"
        maxWidth="480px">
        <div className="flex flex-col gap-4">
          {supplierPaymentMethods.map((paymentMethod: any, i: number) => (
            <div
              className="relative border border-gray-200 rounded-xl p-4 hover:border-gray-300 hover:shadow-md transition-all duration-200"
              key={`buyer_payment_${paymentMethod.id}`}>
              {paymentMethod.isWithdrawFeesOnly && (
                <div className="absolute -top-2 -right-2">
                  <span className="text-[10px] font-semibold text-white px-2 py-0.5 bg-blue-500 rounded-full shadow-sm">
                    For facilitation fees only
                  </span>
                </div>
              )}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  {paymentMethod.type === 'BANK_ACCOUNT' ? (
                    <Bank size={24} className="text-gray-600" />
                  ) : (
                    <CreditCard size={24} className="text-gray-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {paymentMethod.paymentMethodName}
                    </h3>
                    {(paymentMethod.status === 'MICRO_DEPOSITS_SENT' ||
                      paymentMethod.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING' ||
                      paymentMethod.status === 'UNVERIFIED') && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-700">
                        <Warning size={12} />
                        Unverified
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {paymentMethod.type === 'BANK_ACCOUNT'
                      ? 'Bank Account'
                      : paymentMethod.expiry
                      ? `Expires ${paymentMethod.expiry}`
                      : 'Credit Card'}
                  </p>
                </div>
              </div>
              {(paymentMethod.status === 'MICRO_DEPOSITS_SENT' ||
                paymentMethod.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING' ||
                paymentMethod.status === 'UNVERIFIED') && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-3">
                    {paymentMethod.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING'
                      ? 'Two micro-deposits have arrived in your account. Please check and verify them.'
                      : 'Two micro-deposits were sent to this bank account. They should arrive 1-2 business days.'}
                  </p>
                  <DillButton
                    label="Verify Deposits"
                    variant="secondary"
                    isDisabled={paymentMethod.status !== 'MICRO_DEPOSITS_VERIFICATION_PENDING'}
                    onClick={() => {
                      navigate(`${location.pathname}?paymentMethodId=${paymentMethod.id}`);
                      setSelectedPaymentMethodToVerifyMicroTransactions(paymentMethod);
                      setIsAppVerifyDwollaMicroTransactionsModalOpen(true);
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </DillModal>

      <AppVerifyDwollaMicroTransactionsModal
        selectedPaymentMethod={selectedPaymentMethodToVerifyMicroTransactions}
        open={isAppVerifyDwollaMicroTransactionsModalOpen}
        handleClose={() => {
          dispatch(getSupplierPaymentMethods({ supplierId: currentSupplier?.id || '' }));
          dispatch(getSupplierRegistrationStatus({ supplierId: currentSupplier?.id || '' }));
          setSelectedPaymentMethodToVerifyMicroTransactions(undefined);
          setIsAppVerifyDwollaMicroTransactionsModalOpen(false);
        }}
      />

      <SupplierWithdrawModal
        open={isSupplierWithdrawModalOpen}
        handleClose={() => setIsSupplierWithdrawModalOpen(false)}
      />

      <div className="flex-1 min-h-0">
        <DillTable<PaymentRow>
          columns={columns}
          data={paymentRows}
          rowKey={(r) => r.id}
          sortable
          sortCycle="3state"
          scrollX
          search
          searchPlaceholder="Search invoice, buyer..."
          virtualize
          rowHeight={70}
          striped={false}
          rowPadding={0}
          toolbarLeft={toolbarLeft}
          toolbarRight={toolbarRight}
          summaryCards={summaryCards}
          showCount={false}
          resizable
          rowClassName={(row) => (row.id === hoveredPayoutId ? 'bg-slate-50' : '')}
        />
      </div>
    </div>
  );
};
